/**
 * Master lookups and the dashboard's summary counts — the small shared
 * responses many screens load before they render anything else.
 */

import {
  TOUR_AWARD_APPROVALS,
  TOUR_TEMPLATE_APPROVALS,
  TOUR_USER_APPROVALS,
  TOUR_USER_GROUP_APPROVALS,
  TOUR_WORKFLOW_STATUS,
} from './tour-sample-approvals';
import { TOUR_AWARDS, TOUR_AWARD_LISTS } from './tour-sample-awards';
import { TOUR_USERS } from './tour-sample-people';
import { TOUR_TEMPLATES } from './tour-sample-templates';

/** Record statuses (`master/status`). */
export const TOUR_STATUSES = Object.freeze([
  { id: 1, name: 'Active', statusName: 'Active', code: 'ACTIVE' },
  { id: 2, name: 'Inactive', statusName: 'Inactive', code: 'INACTIVE' },
  {
    id: 3,
    name: 'Pending Checker Approval',
    statusName: 'Pending Checker Approval',
    code: 'PENDING',
  },
  { id: 4, name: 'Draft', statusName: 'Draft', code: 'DRAFT' },
  { id: 5, name: 'Rejected', statusName: 'Rejected', code: 'REJECTED' },
]);

/** Employment statuses (`master/employee-status`). */
export const TOUR_EMPLOYEE_STATUSES = Object.freeze([
  { id: 1, name: 'Active', statusName: 'Active' },
  { id: 2, name: 'Resigned', statusName: 'Resigned' },
  { id: 3, name: 'Retired', statusName: 'Retired' },
  { id: 4, name: 'On Leave', statusName: 'On Leave' },
]);

/** The employee picker behind `master/users`. */
export const TOUR_MASTER_USERS = Object.freeze(
  TOUR_USERS.map(user => ({
    id: user.id,
    userId: user.id,
    name: user.fullName,
    fullName: user.fullName,
    employeeId: user.employeeId,
    email: user.email,
    label: `${user.fullName} (${user.employeeId})`,
  }))
);

/**
 * Workflow statuses (`master/workflow-statuses`) — the maker-checker states.
 *
 * The dashboard builds its Maker / Checker switch from these by matching
 * `statusName` against exact strings ('Maker in progress', 'Pending Checker
 * Approval', 'Pending Revision' — note the casing). A status missing that
 * field resolves to no tab id, so the lists query with no status filter and
 * come back empty. The ids must also match the `workflowStatusId` the sample
 * approval rows carry, or the Pending tab shows nothing.
 */
export const TOUR_WORKFLOW_STATUSES = Object.freeze([
  {
    id: 1,
    statusName: 'Maker in progress',
    name: 'Maker in progress',
    code: 'MAKER_IN_PROGRESS',
  },
  {
    // 4, not an arbitrary id: the app buckets the Maker tab as ids [1, 4]
    // (MAKER_IN_PROGRESS_WORKFLOW_STATUS_IDS), so a revision has to be 4 to
    // land there with Maker in progress.
    id: 4,
    statusName: 'Pending Revision',
    name: 'Pending Revision',
    code: 'PENDING_REVISION',
  },
  {
    id: 3,
    statusName: 'Pending Checker Approval',
    name: 'Pending Checker Approval',
    code: 'PENDING_CHECKER',
  },
  { id: 7, statusName: 'Approved', name: 'Approved', code: 'APPROVED' },
  { id: 8, statusName: 'Rejected', name: 'Rejected', code: 'REJECTED' },
]);

/**
 * The module / permission tree the user-group builder renders
 * (`master/system-modules/permissions`).
 */
export const TOUR_SYSTEM_MODULES = Object.freeze([
  {
    moduleId: 1,
    moduleName: 'User Management',
    permissions: [
      { permissionId: 101, name: 'View', code: 'User.View' },
      { permissionId: 102, name: 'Create', code: 'User.Create' },
      { permissionId: 103, name: 'Edit', code: 'User.Edit' },
      { permissionId: 104, name: 'Approve', code: 'User.Approve' },
    ],
  },
  {
    moduleId: 2,
    moduleName: 'Award Management',
    permissions: [
      { permissionId: 201, name: 'View', code: 'Award.View' },
      { permissionId: 202, name: 'Create', code: 'Award.Create' },
      { permissionId: 203, name: 'Approve', code: 'Award.Approve' },
      {
        permissionId: 204,
        name: 'Generate Letter',
        code: 'Award.GenerateLetter',
      },
    ],
  },
  {
    moduleId: 3,
    moduleName: 'Parameters',
    permissions: [
      { permissionId: 301, name: 'View', code: 'Parameter.View' },
      { permissionId: 302, name: 'Edit', code: 'Parameter.Edit' },
    ],
  },
  {
    moduleId: 4,
    moduleName: 'Reports',
    permissions: [
      { permissionId: 401, name: 'View', code: 'Report.View' },
      { permissionId: 402, name: 'Export', code: 'Report.Export' },
    ],
  },
]);

/** Countries / timezones behind the client-onboarding dropdowns. */
export const TOUR_COUNTRIES = Object.freeze([
  { id: 1, code: 'SG', name: 'Singapore', dialCode: '+65' },
  { id: 2, code: 'MY', name: 'Malaysia', dialCode: '+60' },
  { id: 3, code: 'HK', name: 'Hong Kong', dialCode: '+852' },
  { id: 4, code: 'IN', name: 'India', dialCode: '+91' },
  { id: 5, code: 'VN', name: 'Vietnam', dialCode: '+84' },
]);

export const TOUR_TIMEZONES = Object.freeze([
  {
    id: 1,
    code: 'Asia/Singapore',
    name: '(GMT+08:00) Singapore',
    dialCode: '+08:00',
  },
  {
    id: 2,
    code: 'Asia/Kuala_Lumpur',
    name: '(GMT+08:00) Kuala Lumpur',
    dialCode: '+08:00',
  },
  {
    id: 3,
    code: 'Asia/Hong_Kong',
    name: '(GMT+08:00) Hong Kong',
    dialCode: '+08:00',
  },
  {
    id: 4,
    code: 'Asia/Kolkata',
    name: '(GMT+05:30) Kolkata',
    dialCode: '+05:30',
  },
]);

/** Templates offered for award letters and notifications (`master/templates`). */
export const TOUR_MASTER_TEMPLATES = Object.freeze(
  TOUR_TEMPLATES.filter(template => !template.isDraft).map(template => ({
    id: template.templateId,
    templateId: template.templateId,
    name: template.templateName,
    templateName: template.templateName,
    categoryId: template.categoryId,
    categoryName: template.categoryName,
  }))
);

/**
 * Dashboard counts, derived from the approval fixtures so the cards always
 * match the lists behind them.
 */
/** How many of a module's rows sit in a given workflow status. */
const countAtStatus = (
  rows: readonly { workflowStatusId: number }[],
  statusId: number
): number => rows.filter(row => row.workflowStatusId === statusId).length;

const pending = TOUR_WORKFLOW_STATUS.pendingCheckerApproval;
const inProgress = TOUR_WORKFLOW_STATUS.makerInProgress;

/**
 * Dashboard counts, derived from the approval rows rather than written out.
 *
 * The cards and the Maker / Checker tab badges are read straight from here
 * while the lists come from the maintenance endpoints, so a hand-written count
 * shows "Pending Checker Approval (2)" above an empty table.
 */
export const getTourDashboardCounts = () => ({
  userManagement: {
    total: TOUR_USER_APPROVALS.length + TOUR_USER_GROUP_APPROVALS.length,
    userList: TOUR_USER_APPROVALS.length,
    userGroup: TOUR_USER_GROUP_APPROVALS.length,
    bulkUpload: 1,
    pendingCheckerApproval:
      countAtStatus(TOUR_USER_APPROVALS, pending) +
      countAtStatus(TOUR_USER_GROUP_APPROVALS, pending),
    makerInProgress:
      countAtStatus(TOUR_USER_APPROVALS, inProgress) +
      countAtStatus(TOUR_USER_GROUP_APPROVALS, inProgress),
  },
  parameterManagement: {
    total: 6,
    pendingCheckerApproval: 6,
    makerInProgress: 1,
  },
  awards: {
    total: TOUR_AWARD_APPROVALS.length + TOUR_AWARD_LISTS.length,
    award: TOUR_AWARD_APPROVALS.length,
    contingentAward: TOUR_AWARDS.filter(
      award => award.actionStatus === 'Action Required'
    ).length,
    finalAward: 2,
    dividend: 0,
    shareCrediting: 2,
    pendingCheckerApproval: countAtStatus(TOUR_AWARD_APPROVALS, pending),
    makerInProgress: countAtStatus(TOUR_AWARD_APPROVALS, inProgress),
  },
  others: {
    total: TOUR_TEMPLATE_APPROVALS.length,
    template: TOUR_TEMPLATE_APPROVALS.length,
    pendingCheckerApproval: countAtStatus(TOUR_TEMPLATE_APPROVALS, pending),
    makerInProgress: countAtStatus(TOUR_TEMPLATE_APPROVALS, inProgress),
  },
});

/* -------------------------------------------------------------------------- */
/* Help Center plan documents                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The Help & Plans page's document availability. The plans mirror the sample
 * Plan parameter rows so the two screens tell the same story.
 */
export const getTourPlanDocuments = () => ({
  faqMetadata: {
    fileName: 'northwind-share-plan-faq.pdf',
    uploadedBy: 'Sarah Lim',
    uploadedOn: '2025-06-12T10:00:00Z',
    sizeInBytes: 284_160,
  },
  planRules: [
    { planId: 501, planName: 'Northwind Performance Share Plan 2025' },
    { planId: 502, planName: 'Northwind Restricted Share Plan 2025' },
    { planId: 503, planName: 'Northwind Employee Share Option Scheme 2024' },
  ],
  plans: {
    items: [
      {
        planId: 501,
        planName: 'Northwind Performance Share Plan 2025',
        hasFaq: true,
      },
      {
        planId: 502,
        planName: 'Northwind Restricted Share Plan 2025',
        hasFaq: true,
      },
      {
        planId: 503,
        planName: 'Northwind Employee Share Option Scheme 2024',
        hasFaq: false,
      },
    ],
    totalCount: 3,
    offset: 0,
    limit: 10,
    totalPages: 1,
  },
  userGuideAvailability: { isAvailable: true },
});
